import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CompanyUserAvatar } from "@/components/user/company-user-avatar";
import { Check, Clock3, Loader2, Play, UserRoundPlus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMiniTasks } from "~/hooks/use-mini-tasks";
import { MiniTaskCountdown } from "./mini-task-countdown";
import { ReassignMiniTask } from "./reassign-mini-task";

/**
 * Popup encolado de "te asignaron / te reasignaron una mini-tarea". Se muestra
 * SOLO al destinatario (el canal realtime es privado por usuario) y de a uno:
 * la cola del provider va sacando el siguiente al resolver el actual. Sin
 * `alert/confirm/prompt` nativos: es un Dialog del design system.
 *
 * Montar una sola vez en el layout autenticado (dentro de `MiniTasksProvider`).
 */
export function MiniTaskPopup() {
  const ctx = useMiniTasks();
  const { t } = useTranslation("chat");
  const [reassignOpen, setReassignOpen] = useState(false);
  // Qué acción está en vuelo ("start" | "complete"): ambas deshabilitan el
  // footer completo, pero solo el botón pulsado muestra su spinner.
  const [working, setWorking] = useState<"start" | "complete" | null>(null);
  const [expired, setExpired] = useState(false);

  const popup = ctx?.currentPopup ?? null;
  const task = popup ? ctx?.getById(popup.miniTaskId) : undefined;

  // El evento llega antes de que el refetch traiga la tarea: si ya está cargada
  // pero no es accionable (completada, vencida, o reasignada a otra persona), se
  // salta y avanza la cola. Mientras no esté cargada, se espera (no se pinta).
  const isActionable = Boolean(task && task.status === "PENDING");

  // Guarda contra el atasco de la cola: si la tarea encolada NO está en la lista
  // del usuario puede ser "todavía no se refrescó" o "ya salió de mi lista"
  // (reasignada a otro / cancelada). Cuando un popup llega a la cabeza y su tarea
  // falta, forzamos UN refetch y guardamos el `dataUpdatedAt` previo; si al
  // asentarse (sin fetch en vuelo y con `dataUpdatedAt` ya avanzado) la tarea
  // sigue ausente, descartamos y avanzamos. Así un refetch disparado por el
  // propio evento que SÍ trae la tarea la muestra, sin descartes prematuros.
  const settleRef = useRef<{ id: string; baseline: number } | null>(null);

  useEffect(() => {
    if (!ctx || !popup) {
      settleRef.current = null;
      return;
    }

    const current = ctx.getById(popup.miniTaskId);
    if (current) {
      if (current.status !== "PENDING") {
        ctx.dismissPopup();
      }
      return;
    }

    // Tarea ausente en la cabeza de la cola.
    const settle = settleRef.current;
    if (settle?.id !== popup.miniTaskId) {
      settleRef.current = { id: popup.miniTaskId, baseline: ctx.dataUpdatedAt };
      void ctx.refetchList();
      return;
    }
    if (!ctx.isFetching && ctx.dataUpdatedAt > settle.baseline) {
      ctx.dismissPopup();
    }
  }, [ctx, popup]);

  if (!ctx || !popup || !task || !isActionable) {
    return null;
  }

  const advance = () => {
    setReassignOpen(false);
    ctx.dismissPopup();
  };

  const handleComplete = async () => {
    setWorking("complete");
    try {
      await ctx.complete(task.id);
      advance();
    } catch {
      // El `onError` centralizado ya avisó y resincronizó la lista; mantenemos el
      // popup abierto (no avanzamos) para que el usuario reintente o lo cierre.
    } finally {
      setWorking(null);
    }
  };

  const handleStart = async () => {
    setWorking("start");
    try {
      await ctx.start(task.id);
      advance();
    } catch {
      // @expected-error-boundary Igual que `handleComplete`: el toast lo emite el
      // `onError` central del provider; el popup queda abierto para reintentar o cerrar.
    } finally {
      setWorking(null);
    }
  };

  const handleReassigned = async (toUserId: string) => {
    // Propaga el rechazo al picker (ReassignMiniTask) para que NO cierre su diálogo
    // en error; el toast lo emite el `onError` centralizado. Solo avanzamos en éxito.
    await ctx.reassign(task.id, toUserId);
    advance();
  };

  const assignerName = task.createdBy?.name ?? t("mini_tasks.popup.someone", "Alguien");

  return (
    <>
      <Dialog open onOpenChange={(next) => (!next ? advance() : undefined)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock3 className="text-primary h-5 w-5" />
              {popup.event === "reassigned"
                ? t("mini_tasks.popup.title_reassigned", "Te pasaron una mini-tarea")
                : t("mini_tasks.popup.title_assigned", "Tienes una mini-tarea")}
            </DialogTitle>
            <DialogDescription className="flex items-center gap-2 pt-1">
              <CompanyUserAvatar
                userId={task.createdBy ? Number(task.createdBy.id) : undefined}
                name={assignerName}
                avatar={task.createdBy?.avatar ?? null}
                className="h-5 w-5 text-[10px]"
              />
              {t("mini_tasks.popup.from", { name: assignerName, defaultValue: "De {{name}}" })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-1">
            <p className="text-base font-medium break-words">{task.title}</p>
            <div className="bg-muted/50 flex items-center justify-between rounded-md px-3 py-2 text-sm">
              <span className="text-muted-foreground">
                {t("mini_tasks.popup.expires_in", "Vence en")}
              </span>
              <MiniTaskCountdown
                key={task.id}
                expiresAt={task.expiresAt}
                expiredLabel={t("mini_tasks.expired", "Vencida")}
                className="text-base font-semibold"
                onExpiredChange={setExpired}
              />
            </div>
          </div>

          <DialogFooter className="flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="shrink-0"
              title={t("mini_tasks.actions.pass", "Pasar a otro")}
              aria-label={t("mini_tasks.actions.pass", "Pasar a otro")}
              disabled={working !== null || expired}
              onClick={() => setReassignOpen(true)}
            >
              <UserRoundPlus className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              disabled={working !== null || expired}
              onClick={handleStart}
            >
              {working === "start" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-2 h-4 w-4" />
              )}
              {t("mini_tasks.actions.start", "Empezar")}
            </Button>
            <Button
              type="button"
              className="flex-1"
              disabled={working !== null || expired}
              onClick={handleComplete}
            >
              {working === "complete" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Check className="mr-2 h-4 w-4" />
              )}
              {t("mini_tasks.actions.complete", "Completar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReassignMiniTask
        miniTaskId={task.id}
        open={reassignOpen}
        onOpenChange={setReassignOpen}
        excludeUserId={task.assignedTo?.id ?? null}
        onReassign={handleReassigned}
      />
    </>
  );
}
